import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Client } from "@langchain/langgraph-sdk";
import { useStream } from "@langchain/langgraph-sdk/react";
import { v4 as uuidv4 } from "uuid";
import { TextField, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import ThreadSidebar from "./ThreadSidebar";
import { ensureToolCallsHaveResponses } from "./chatUtils";
import { auth, db } from "../../firebase";
import { doc, setDoc, updateDoc, getDoc, deleteField, Timestamp } from "firebase/firestore";
import { useUserAuth } from "../../context/UserAuthContext";
import { LANGGRAPH_API_URL, LANGGRAPH_ASSISTANT_ID } from "../../config";
import useIsMobile from "../../hooks/useIsMobile";

const ChatPage = () => {
  const { user, orgData } = useUserAuth();
  const isMobile = useIsMobile();
  const [threadId, setThreadId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [decisions, setDecisions] = useState([]);
  const [feedback, setFeedback] = useState({});
  const titleSetRef = useRef(false);
  const newThreadRef = useRef(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [interruptFields, setInterruptFields] = useState({});

  const agentEntries = useMemo(() => {
    const agents = orgData?.agents || {};
    return Object.entries(agents);
  }, [orgData]);

  // Auto-select first enabled agent (or first agent if none enabled)
  useEffect(() => {
    if (agentEntries.length === 0) { setSelectedAgentId(null); return; }
    const enabled = agentEntries.find(([, a]) => a.enabled);
    setSelectedAgentId(enabled ? enabled[0] : agentEntries[0][0]);
  }, [agentEntries]);

  const client = useMemo(
    () =>
      new Client({
        apiUrl: LANGGRAPH_API_URL,
        apiKey: null,
        onRequest: async (_url, init) => {
          const token = await auth.currentUser?.getIdToken();
          if (token) {
            init.headers = { ...init.headers, Authorization: `Bearer ${token}` };
          }
          return init;
        },
      }),
    []
  );

  // Clear decisions, feedback, and title-set flag when thread changes
  useEffect(() => {
    setDecisions([]);
    setFeedback({});
    titleSetRef.current = false;

    // Skip feedback read for brand-new threads (doc doesn't exist yet)
    if (!threadId || newThreadRef.current) {
      newThreadRef.current = false;
      return;
    }
    getDoc(doc(db, "chatThreads", threadId))
      .then((snap) => {
        if (snap.exists() && snap.data().feedback) {
          setFeedback(snap.data().feedback);
        }
      })
      .catch((err) => console.error("Failed to load feedback:", err));
  }, [threadId]);

  const handleThreadId = useCallback(
    async (id) => {
      newThreadRef.current = true;
      setThreadId(id);
      if (!id || !user?.uid) return;
      try {
        const ref = doc(db, "chatThreads", id);
        const data = {
          userId: user.uid,
          title: "",
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        if (selectedAgentId) data.agentId = selectedAgentId;
        await setDoc(ref, data);
      } catch (err) {
        console.error("Failed to create chat thread doc:", err);
      }
    },
    [user, selectedAgentId]
  );

  const handleFeedback = useCallback(
    async (messageId, rating) => {
      if (!threadId) return;
      const ref = doc(db, "chatThreads", threadId);
      const fieldPath = `feedback.${messageId}`;
      if (rating) {
        setFeedback((prev) => ({ ...prev, [messageId]: rating }));
        await updateDoc(ref, { [fieldPath]: rating }).catch((err) =>
          console.error("Failed to save feedback:", err)
        );
      } else {
        setFeedback((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
        await updateDoc(ref, { [fieldPath]: deleteField() }).catch((err) =>
          console.error("Failed to remove feedback:", err)
        );
      }
    },
    [threadId]
  );

  const stream = useStream({
    client,
    assistantId: LANGGRAPH_ASSISTANT_ID,
    threadId,
    onThreadId: handleThreadId,
    onError: (err) => console.error("Stream error:", err),
  });

  const handleSubmit = useCallback(
    async (input) => {
      const safeMessages = ensureToolCallsHaveResponses(stream.messages);
      stream.submit(input, {
        optimisticValues: (prev) => ({
          ...prev,
          messages: [
            ...(safeMessages || []),
            ...(input.messages || []),
          ],
        }),
      });

      // Persist title (first human message) and updatedAt
      if (threadId && user?.uid) {
        try {
          const ref = doc(db, "chatThreads", threadId);
          if (!titleSetRef.current && input.messages?.[0]?.content) {
            const title = input.messages[0].content.slice(0, 60);
            await updateDoc(ref, {
              title,
              updatedAt: Timestamp.now(),
            });
            titleSetRef.current = true;
          } else {
            await updateDoc(ref, { updatedAt: Timestamp.now() });
          }
        } catch (err) {
          console.error("Failed to update chat thread doc:", err);
        }
      }
    },
    [stream, threadId, user]
  );

  const handleStop = useCallback(() => {
    stream.stop();
  }, [stream]);

  // Determine interrupt type: fields interrupt only if value has a fields array, otherwise approval
  const interruptValue = stream.interrupt?.value;
  const interruptFields_ = interruptValue?.fields;
  const isFieldsInterrupt = stream.interrupt && !stream.isLoading && Array.isArray(interruptFields_) && interruptFields_.length > 0;
  const isApprovalInterrupt = stream.interrupt && !stream.isLoading && !isFieldsInterrupt;
  const interruptMessage = typeof interruptValue === "string"
    ? interruptValue
    : interruptValue?.message || interruptValue?.question || null;

  // Reset field inputs when interrupt changes
  useEffect(() => {
    setInterruptFields({});
  }, [stream.interrupt]);

  const handleApprove = useCallback(() => {
    const lastAiMsg = [...(stream.messages || [])]
      .reverse()
      .find((m) => m.type === "ai");
    setDecisions((prev) => [
      ...prev,
      {
        id: uuidv4(),
        content: "Approved the quote",
        type: "approve",
        afterMessageId: lastAiMsg?.id || null,
      },
    ]);
    stream.submit(null, { command: { resume: true } });
  }, [stream]);

  const handleReject = useCallback(() => {
    const lastAiMsg = [...(stream.messages || [])]
      .reverse()
      .find((m) => m.type === "ai");
    setDecisions((prev) => [
      ...prev,
      {
        id: uuidv4(),
        content: "Rejected the quote",
        type: "reject",
        afterMessageId: lastAiMsg?.id || null,
      },
    ]);
    stream.submit(null, { command: { resume: false } });
  }, [stream]);

  const handleFieldsSubmit = useCallback(() => {
    stream.submit(null, { command: { resume: interruptFields } });
    setInterruptFields({});
  }, [stream, interruptFields]);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <ThreadSidebar
        userId={user?.uid}
        threadId={threadId}
        onSelectThread={(id) => { setThreadId(id); if (isMobile) setSidebarOpen(false); }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        agentId={selectedAgentId}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? "10px 12px" : "12px 24px",
            borderBottom: "1px solid #e8e8e8",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            style={{
              width: "32px",
              height: "32px",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              backgroundColor: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={sidebarOpen ? "Hide threads" : "Show threads"}
          >
            <MenuIcon style={{ fontSize: "18px", color: "#666" }} />
          </button>
          <SmartToyOutlinedIcon style={{ fontSize: "20px", color: "#825ee7" }} />
          <span style={{ fontWeight: 600, fontSize: "15px", color: "#333" }}>
            Agent Chat
          </span>
          {agentEntries.length > 0 && (
            <TextField
              select
              size="small"
              value={selectedAgentId || ""}
              onChange={(e) => {
                setSelectedAgentId(e.target.value);
                setThreadId(null);
              }}
              sx={{
                minWidth: "220px",
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            >
              {agentEntries.map(([id, agent]) => (
                <MenuItem key={id} value={id}>{agent.name || "Unnamed Agent"}</MenuItem>
              ))}
            </TextField>
          )}
          {threadId && (
            <span style={{ fontSize: "12px", color: "#999", marginLeft: "auto" }}>
              {threadId.slice(0, 8)}...
            </span>
          )}
        </div>

        {/* Messages */}
        <ChatMessages
          messages={stream.messages}
          isLoading={stream.isLoading}
          decisions={decisions}
          feedback={feedback}
          onFeedback={handleFeedback}
        />

        {/* Error */}
        {stream.error && (
          <div
            style={{
              padding: isMobile ? "8px 12px" : "8px 24px",
              backgroundColor: "#fef2f2",
              borderTop: "1px solid #fecaca",
              color: "#dc2626",
              fontSize: "13px",
            }}
          >
            Error: {stream.error.message || String(stream.error)}
          </div>
        )}

        {/* Approval bar — shown when the agent is paused on an approval interrupt */}
        {isApprovalInterrupt && (
          <div
            style={{
              padding: isMobile ? "12px" : "12px 24px",
              backgroundColor: "#f9f8fc",
              borderTop: "1px solid #e8e8e8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "14px", color: "#555", marginRight: "4px" }}>
              Quote ready for review
            </span>
            <button
              onClick={handleApprove}
              style={{
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "#16a34a",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Approve Quote
            </button>
            <button
              onClick={handleReject}
              style={{
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#dc2626",
                backgroundColor: "transparent",
                border: "2px solid #dc2626",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Reject Quote
            </button>
          </div>
        )}

        {/* Fields interrupt — agent is asking for required field inputs */}
        {isFieldsInterrupt && (
          <div
            style={{
              padding: isMobile ? "12px" : "16px 24px",
              backgroundColor: "#f9f8fc",
              borderTop: "1px solid #e8e8e8",
              flexShrink: 0,
            }}
          >
            {interruptMessage && (
              <div style={{ fontSize: "14px", color: "#555", marginBottom: "12px", fontWeight: 500 }}>
                {interruptMessage}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
              {interruptFields_.map((field) => {
                const name = typeof field === "string" ? field : (field.name || field.label || String(field));
                const label = typeof field === "object" ? (field.label || field.name || String(field)) : field;
                return (
                  <div key={name}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#555", marginBottom: "4px" }}>
                      {label}
                    </label>
                    <input
                      type="text"
                      value={interruptFields[name] || ""}
                      onChange={(e) => setInterruptFields((prev) => ({ ...prev, [name]: e.target.value }))}
                      placeholder={`Enter ${label}`}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        fontSize: "14px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleFieldsSubmit}
              style={{
                padding: "8px 20px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "#825ee7",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Submit
            </button>
          </div>
        )}

        {/* Input — disabled during interrupts */}
        <ChatInput
          onSubmit={handleSubmit}
          onStop={handleStop}
          isLoading={stream.isLoading}
          disabled={!!isApprovalInterrupt || !!isFieldsInterrupt}
        />
      </div>
    </div>
  );
};

export default ChatPage;
